import { useCustomerUsers } from '@src/hooks/useCustomer';
import {
  FilterClauseType,
  IconName,
  PricingModeEnum,
  QUOTE_STATUS_MAPPING,
  QuoteStatusEnum,
  SideEnum,
  TWO_WAY_SIDE,
  cleanupInitialFilterDateRange,
  filterExistsAndExcludes,
  getTypedKeys,
  removeEmptyFilters,
  useDateRangeFilter,
  useSymbolsFilter,
  type BlotterTableClientLocalFilter,
  type BlotterTableFilter,
  type BlotterTableFiltersProps,
  type DateRangeFilter,
  type FilterClause,
  type FilterableProperty,
  type UseFilterBuilderProps,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { compact, isEqual, keys, startCase, uniq, values } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import { useCounterpartyFilter, useIDFilter, useSidesFilter, useUsersFilter } from '../../../../hooks';
import { useMarketAccountsFilter } from '../../../../hooks/filters/useMarketAccountsFilter';
import type { ManualRFQ } from './types';

export interface ManualRFQsBlotterFilter extends DateRangeFilter {
  Counterparty?: string[];
  MarketAccounts?: string[];
  Sides?: string[];
  Symbols?: string[];
  /** These are the quote status texts we are filtering by on the client side */
  _statuses?: string[];
  Users?: string[];
  CustomerUsers?: string[];
  RFQID?: string;
  PricingMode?: string[];
}

export interface UseManualRFQsBlotterFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}
export function useManualRFQsBlotterFilter({ persistedBlotterTable }: UseManualRFQsBlotterFilterParams) {
  const { onFilterChanged: saveFilter } = persistedBlotterTable;
  const [initialFilter] = useState(() => cleanupInitialFilterDateRange(persistedBlotterTable.initialState?.filter));
  const [filter, setFilter] = useState<ManualRFQsBlotterFilter>((initialFilter as ManualRFQsBlotterFilter) || {});

  const changeFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const priorFilter = filter;
      const newFilter = action instanceof Function ? action(filter) : action;

      if (!isEqual(priorFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [filter, saveFilter]
  );

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<ManualRFQ>>(
    row => {
      const { data } = row;
      if (filterExistsAndExcludes(filter, 'MarketAccounts', data, 'MarketAccount')) {
        return false;
      }
      if (
        filterExistsAndExcludes(
          {
            Sides: filter.Sides?.map(side => (side === TWO_WAY_SIDE ? '' : side)),
          },
          'Sides',
          data,
          'tradedSideOrSide'
        )
      ) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Symbols', data, 'Symbol')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'PricingMode', data, 'PricingMode')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'CustomerUsers', data, 'CustomerUser')) {
        return false;
      }
      return true;
    },
    [filter]
  );

  const usersFilter = useUsersFilter();
  const counterpartyFilter = useCounterpartyFilter({ label: 'Customer' });
  const symbolFilter = useSymbolsFilter();
  const marketAccountsFilter = useMarketAccountsFilter();
  const customerUsers = useCustomerUsers();
  const sidesFilter = useSidesFilter();
  const idFilter = useIDFilter();

  const filterableProperties = useMemo(
    () =>
      compact<FilterableProperty<keyof ManualRFQsBlotterFilter>>([
        counterpartyFilter,
        marketAccountsFilter,
        { ...sidesFilter, options: [TWO_WAY_SIDE, ...values(SideEnum)] },
        symbolFilter,
        {
          key: '_statuses',
          label: 'Status',
          icon: IconName.CheckCircle,
          options: [...keys(QUOTE_STATUS_MAPPING), ...keys(QuoteStatusEnum)],
          getOptionLabel: (option: string) => startCase(option),
        },
        usersFilter,
        {
          key: 'CustomerUsers',
          label: 'Customer User',
          icon: IconName.User,
          options: customerUsers?.map(c => c.Email) ?? [],
          getOptionLabel: (option: string) => option,
          clearUnsupportedSelectionsOnInit: false,
        },
        {
          key: 'PricingMode',
          label: 'Pricing Mode',
          icon: IconName.CurrencyDollar,
          options: values(PricingModeEnum),
          getOptionLabel: startCase,
        },
        { ...idFilter, key: 'RFQID', label: 'RFQ ID' },
      ]),
    [counterpartyFilter, marketAccountsFilter, symbolFilter, usersFilter, customerUsers, sidesFilter, idFilter]
  );

  const initialFilterClauses = useMemo(() => {
    if (!filter) {
      return [];
    }

    const clauses: FilterClause[] = [];
    getTypedKeys(filter).forEach(key => {
      // We have date range keys in the filter too
      const keyIsFilterableProperty = filterableProperties.some(property => property.key === key);
      if (keyIsFilterableProperty) {
        clauses.push({
          key: key,
          type: FilterClauseType.INCLUSIVE,
          selections: filter[key] as string[],
        });
      }
    });

    return clauses;
  }, [filter, filterableProperties]);

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter: ManualRFQsBlotterFilter = removeEmptyFilters<ManualRFQsBlotterFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies ManualRFQsBlotterFilter),
          RFQID: filterClausesByPropertyKey.get('RFQID')?.selections?.[0],
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );
  const dateRangeFilter = useDateRangeFilter(filter, changeFilter);

  const filterBuilderProps = useMemo(
    () => ({
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    }),
    [filterableProperties, handleFilterClausesChanged, initialFilterClauses]
  ) satisfies UseFilterBuilderProps;
  const blotterTableFilterProps = useMemo(
    () => ({
      ...dateRangeFilter,
    }),
    [dateRangeFilter]
  ) satisfies Partial<BlotterTableFiltersProps>;
  return {
    filter,
    clientSideFilter,
    filterBuilderProps,
    blotterTableFilterProps,
  };
}

const colIDToFilterBuilderKeyMap = {
  Counterparty: 'Counterparty',
  MarketAccount: 'MarketAccounts',
  Side: 'Sides',
  Symbol: 'Symbols',
  QuoteStatus: '_statuses',
  User: 'Users',
  CustomerUser: 'CustomerUsers',
  RFQID: 'RFQID',
} satisfies Partial<Record<keyof ManualRFQ, keyof ManualRFQsBlotterFilter>>;

export function colIDToFilterBuilderKey(id: string): keyof ManualRFQsBlotterFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap];
}

// Essentially what we'll be amending to the CustomerQuote backend request
type CustomerQuotesServerFilter = Pick<ManualRFQsBlotterFilter, 'StartDate' | 'EndDate' | 'Users' | 'RFQID'> & {
  Statuses?: string[];
  Counterparty?: string;
};

export function onlyServerFilterKeys(filter: ManualRFQsBlotterFilter | undefined) {
  if (!filter) {
    return filter;
  }

  const serverFilter = {
    StartDate: filter.StartDate,
    EndDate: filter.EndDate,
    Statuses: uniq(filter._statuses?.flatMap(s => (isQuoteStatusKey(s) ? QUOTE_STATUS_MAPPING[s] : s))),
    Users: filter.Users,
    RFQID: filter.RFQID,
    Counterparty: filter.Counterparty?.at(0),
  } satisfies CustomerQuotesServerFilter;

  return serverFilter;
}

const isQuoteStatusKey = (key: string): key is keyof typeof QUOTE_STATUS_MAPPING => key in QUOTE_STATUS_MAPPING;
